const isWanPlatform = platform => platform === 'wan3' || platform === 'wan3-cd'

const getWanReferenceError = (platform, references = {}) => {
  if (!isWanPlatform(platform)) return ''
  const hasReference = ['images', 'videos', 'audios'].some(category =>
    (references?.[category] || []).some(item => item?.file ||
      (typeof item?.url === 'string' && item.url.trim() && !item.url.trim().startsWith('blob:'))))
  return hasReference ? '' : '请至少添加一个参考图片、音频或视频后再生成'
}

const WAN3_LIMITS = Object.freeze({
  images: 10,
  videos: 5,
  audios: 5,
  imageBytes: 20 * 1024 * 1024,
  audioBytes: 15 * 1024 * 1024,
  minMediaSeconds: 1,
  maxMediaSeconds: 15,
  maxAggregateSeconds: 15
})

const IMAGE_EXTENSION_RE = /\.(jpe?g|png|bmp|webp)$/i
const VIDEO_EXTENSION_RE = /\.(mp4|mov)$/i
const AUDIO_EXTENSION_RE = /\.(wav|mp3)$/i

const fileName = file => String(file?.name || '').trim().split(/[?#]/)[0]
const fileType = file => String(file?.type || '').trim().toLowerCase()

const classifyWanReferenceFile = file => {
  const name = fileName(file)
  const type = fileType(file)
  if ((type.startsWith('image/') || !type) && IMAGE_EXTENSION_RE.test(name)) return 'image'
  if ((type === 'video/mp4' || type === 'video/quicktime' || !type) && VIDEO_EXTENSION_RE.test(name)) return 'video'
  if ((type === 'audio/wav' || type === 'audio/x-wav' || type === 'audio/mpeg' || !type) && AUDIO_EXTENSION_RE.test(name)) return 'audio'
  return ''
}

const validateWanImage = file => {
  if (classifyWanReferenceFile(file) !== 'image') {
    return { ok: false, message: '图片仅支持 JPEG、JPG、PNG、BMP 或 WEBP 格式' }
  }
  if ((Number(file?.size) || 0) > WAN3_LIMITS.imageBytes) {
    return { ok: false, message: '单张图片不能超过20MB' }
  }
  return { ok: true, message: '' }
}

// Wan 3.0 validates the actual reference media duration from the submitted URL.
// Browser metadata is optional and may be unavailable, so only validate type and size here.
const validateWanTimedMedia = (file, mediaType) => {
  if (!['video', 'audio'].includes(mediaType) || classifyWanReferenceFile(file) !== mediaType) {
    return { ok: false, message: mediaType === 'audio' ? '音频仅支持 WAV 或 MP3 格式' : '视频仅支持 MP4 或 MOV 格式' }
  }
  if (mediaType === 'audio' && (Number(file?.size) || 0) > WAN3_LIMITS.audioBytes) {
    return { ok: false, message: '单个音频不能超过15MB' }
  }
  return { ok: true, message: '' }
}

const validateWanAggregateDuration = items => {
  const duration = (items || []).reduce((sum, item) => sum + (Number(item?.duration) || 0), 0)
  // Reference duration is validated by Wan 3.0 from the submitted URLs.
  return { ok: true, message: '', duration }
}

const toWanReferencePrompt = prompt => String(prompt || '')
  .replace(/@图片(\d+)/g, '图 $1')
  .replace(/@视频(\d+)/g, '视频 $1')
  .replace(/@音频(\d+)/g, '音频 $1')

const normalizedRole = role => String(role || 'reference').trim().toLowerCase()

const assertWanReferenceCounts = ({ images = [], videos = [], audios = [] } = {}) => {
  if (images.length > WAN3_LIMITS.images) throw new Error(`Wan 3.0 最多支持${WAN3_LIMITS.images}张参考图片`)
  if (videos.length > WAN3_LIMITS.videos) throw new Error(`Wan 3.0 最多支持${WAN3_LIMITS.videos}个参考视频`)
  if (audios.length > WAN3_LIMITS.audios) throw new Error(`Wan 3.0 最多支持${WAN3_LIMITS.audios}个参考音频`)
}

const buildWanMedia = ({ images = [], videos = [], audios = [] } = {}) => {
  assertWanReferenceCounts({ images, videos, audios })
  const media = []
  images.forEach(image => {
    const role = normalizedRole(image?.role)
    if (!['reference', 'reference_image', 'ref'].includes(role)) {
      throw new Error('Wan 3.0 当前只开放全能参考图片，不支持首帧或尾帧模式')
    }
    if (image?.url) media.push({ type: 'reference_image', url: image.url })
  })
  videos.forEach(video => {
    if (video?.url) media.push({ type: 'reference_video', url: video.url })
  })
  audios.forEach(audio => {
    if (audio?.url) media.push({ type: 'reference_audio', url: audio.url })
  })
  return media
}

module.exports = {
  isWanPlatform,
  getWanReferenceError,
  WAN3_LIMITS,
  assertWanReferenceCounts,
  buildWanMedia,
  classifyWanReferenceFile,
  toWanReferencePrompt,
  validateWanAggregateDuration,
  validateWanImage,
  validateWanTimedMedia
}
